
    /*global server*/
    import { defineComponent } from 'vue';
    import { Form } from "vee-validate";
    import { AxiosError, AxiosResponse } from 'axios';
    import { useProgrammatic } from '@oruga-ui/oruga-next';
    import * as yup from "yup";
    import OutsideAccount from '../OutsideAccount/outside-account.vue';
    import { debounce } from "debounce";

    const { oruga } = useProgrammatic();

    type Nullable<T> = null | T;

    export default defineComponent({
        components: {
            Form,
            OutsideAccount
        },
        data() {
            function toast() {
                oruga.notification.open({
                    message: 'Account Created Successfully!',
                    rootClass: 'toast-notification',
                    position: 'top',
                    duration: 5000,
                    variant: 'success'
                });
            }

            return {
                toast,
                form: {
                    LastName: '',
                    FirstName: '',
                    MI: '',
                    Suffix: '',
                    EmailAddress: '',
                    UserID: '',
                    Password: ''
                } as server.Accounts,
                passwordStrength: 0,
                isSubmitPageLoading: false as boolean,
                isUserIdAvailable: true as boolean,
                isUserIdChecking: false as boolean,
                validateUserId: null as Nullable<Function>,
                isEmailAddressAvailable: true as boolean,
                isEmailAddressChecking: false as boolean,
                validateEmailAddress: null as Nullable<Function>
            }
        },
        computed: {
            schema(): object {
                const self: any = this;

                return yup.object().shape({
                    LastName: yup
                        .string()
                        .required("Last name is required")
                        .max(50, "Max allowed Charactors is 50"),
                    FirstName: yup
                        .string()
                        .required("First name is required")
                        .max(25, "Max allowed Charactors is 25"),
                    MI: yup
                        .string()
                        .max(1, "Max allowed Charactors is 1"),
                    Suffix: yup
                        .string()
                        .max(4, "Max allowed Charactors is 4"),
                    EmailAddress: yup
                        .string()
                        .required("Email address is required")
                        .email("Must be a valid email address")
                        .max(200, "Max allowed Charactors is 200")
                        .test('emailExists', 'Email already in use',
                            () => {
                                return self.isEmailAddressAvailable
                            }
                        ),
                    UserID: yup
                        .string()
                        .required("UserID is required")
                        .max(50, "Max allowed Charactors is 50")
                        .test('validUserId', 'UserId unavailable',
                            () => {
                                return self.isUserIdAvailable
                            }
                        ),
                    Password: yup
                        .string()
                        .required("Password is required")
                        .max(200, "Max allowed Charactors is 200"),
                    confirmPassword: yup
                        .string()
                        .oneOf([yup.ref('Password'), null], "Passwords don't match")
                        .required("Password is required")
                        .max(200, "Max allowed Charactors is 200"),
                });
            },
        },
        methods: {
            onSubmit(): void {
                this.isSubmitPageLoading = true;

                this.$axios
                    .post('account/createAccount', this.form)
                    .then(({ data }: AxiosResponse<server.Accounts>) => {
                        if (data.Id > 0) {
                            // account creation successful //
                            this.toast();
                            this.clearfields();
                            this.$router.push('/');
                        }
                        else {
                            // account creation unsuccessful //
                        }
                    })
                    .catch((error: AxiosError) => {
                        console.log(error);
                    })
                    .finally(() => {
                        this.isSubmitPageLoading = false;
                    });
            },
            clearfields(): void {
                (this.$refs.formValidator as any).resetForm();
            },
            validateUserIdInput({ target }: InputEvent): void {

                const userId = (target as any).value;

                if (userId != '') {
                    this.isUserIdChecking = true;

                    // --- Set the 'Checking userId availability' error message while checking the database. --- //
                    (this.$refs.formValidator as any).setFieldError('UserID', 'Checking userId availability.');

                    this.$axios
                        .get('account/GetAccountUserIdStatusByUserId', {
                            params: {
                                userId: userId
                            }
                        })
                        .then(({ data }: AxiosResponse<boolean>) => {
                            this.isUserIdAvailable = data;
                        })
                        .finally(() => {
                            let outsideAccountThis = (this.$refs.outsideAccount as any);
                            // --- Clear all errors from UserId Field. --- //
                            outsideAccountThis.$refs.userIdInput.errors = [];

                            // --- give a slight delay so user can read manually added error message. --- //
                            setTimeout(() => {
                                // --- Manually trigger validation --- //
                                outsideAccountThis.$refs.userIdInput.validate();

                                this.isUserIdChecking = false;

                            }, 250);
                        });
                }
                else {
                    this.isUserIdAvailable = true;
                }
            },
            validateIfEmailAddressInput({ target }: InputEvent): void {

                const emailAddress = (target as any).value;

                if (emailAddress != '') {
                    this.isEmailAddressChecking = true;

                    // --- Set the 'Checking email availability' error message while checking the database. --- //
                    (this.$refs.formValidator as any).setFieldError('EmailAddress', 'Checking userId availability.');

                    this.$axios
                        .get('account/CheckIfEmailExists', {
                            params: {
                                emailToCheck: emailAddress
                            }
                        })
                        .then(({ data }: AxiosResponse<boolean>) => {
                            this.isEmailAddressAvailable = !data;
                        })
                        .finally(() => {
                            let outsideAccountThis = (this.$refs.outsideAccount as any);
                            // --- Clear all errors from emailAddress Field. --- //
                            outsideAccountThis.$refs.emailAddressInput.errors = [];

                            // --- give a slight delay so user can read manually added error message. --- //
                            setTimeout(() => {
                                // --- Manually trigger validation --- //
                                outsideAccountThis.$refs.emailAddressInput.validate();

                                this.isEmailAddressChecking = false;

                            }, 250);
                        });
                }
                else {
                    this.isEmailAddressAvailable = true;
                }
            }
        },
        created() {
            this.validateUserId = debounce(this.validateUserIdInput, 500);

            this.validateEmailAddress = debounce(this.validateIfEmailAddressInput, 500);
        }
    });
